import type { QueryClient } from '@tanstack/react-query';
import { getCurrentUser } from '@/integrations/idb/models/users';
import { tryCatch } from '@/lib/try-catch';
import type { ExternalTarget } from '../consts/external-pages';
import type { ExternalEntryParams } from './external-entry-params';
import { fetchCurrentUser } from '../services/fetch-current-user';
import { refreshTokens } from '../services/refresh-tokens';
import { clearAuthSession } from './clear-auth-session';
import { establishSession, SessionConflictError } from './establish-session';
import { isPastClearDeadline } from './refresh-session';

type ExternalEntryOutcome =
  | { type: 'redirect'; to: ExternalTarget }
  | { type: 'invalidLink'; signedIn: boolean; target?: ExternalTarget }
  | { type: 'identityConflict' };

const hasSession = async () => Boolean((await getCurrentUser())?.token);

const clearExpiredSession = async (queryClient: QueryClient) => {
  const current = await getCurrentUser();

  if (current && isPastClearDeadline(current)) {
    await clearAuthSession(queryClient);
  }
};

export const runExternalEntry = async (
  params: ExternalEntryParams | undefined,
  queryClient: QueryClient,
): Promise<ExternalEntryOutcome> => {
  await clearExpiredSession(queryClient);

  if (!params) return { type: 'invalidLink', signedIn: await hasSession() };

  const invalidLink = async (): Promise<ExternalEntryOutcome> => ({
    type: 'invalidLink',
    signedIn: await hasSession(),
    target: params.target,
  });

  const { data: tokens, error: refreshError } = await tryCatch(
    refreshTokens({ token: params.token, refreshToken: params.refreshToken }),
  );

  if (refreshError) return invalidLink();

  const { data: user, error: fetchUserError } = await tryCatch(
    fetchCurrentUser(tokens.token),
  );

  if (fetchUserError) return invalidLink();

  const { error: sessionError } = await tryCatch(
    establishSession({ ...user, ...tokens }),
  );

  if (sessionError instanceof SessionConflictError) {
    return { type: 'identityConflict' };
  }

  if (sessionError) throw sessionError;

  return { type: 'redirect', to: params.target };
};
